import type { ShiftLookupMaps } from "@/utils/shift-util";
import {
  getManpowerBucketForResolvedShift,
  resolveShiftFromScheduleCellFromMaps,
} from "@/utils/shift-util";
import type { BuildCellMapOptions, NurseForCellMap } from "./types";

type AvailableShiftItem = BuildCellMapOptions["availableShifts"][number];

export type ManpowerAggregation = {
  readonly validateKeys: Set<string>;
  readonly groupLetterMap: ReadonlyMap<string, string>;
};

export function buildGroupTotalsByDay(
  nurses: NurseForCellMap[],
  lookupMaps: ShiftLookupMaps<AvailableShiftItem>,
  daysInMonth: number,
  precepteePolicy: BuildCellMapOptions["precepteePolicy"],
  manpowerAgg?: ManpowerAggregation,
  nurseLockedDaysMap?: Map<string, Set<number>>
): Map<string, number> {
  const totals = new Map<string, number>();

  const nursesToUse = precepteePolicy.excludePrecepteeFromCount
    ? nurses.filter((nurse) => nurse.preceptor?.role !== "preceptee")
    : nurses;

  for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
    const groupCounts = new Map<string, number>();

    nursesToUse.forEach((nurse) => {
      const nurseId = nurse.nurse_id || nurse.id;
      if (nurseId && nurseLockedDaysMap?.get(nurseId)?.has(dayIndex + 1)) {
        return;
      }

      const code = nurse.schedule?.[dayIndex];
      if (!code) return;

      const scheduleId = nurse.schedule_ids?.[dayIndex] ?? null;
      const resolved = resolveShiftFromScheduleCellFromMaps(
        code,
        scheduleId,
        lookupMaps
      );
      const bucket = manpowerAgg
        ? getManpowerBucketForResolvedShift(
            resolved,
            manpowerAgg.groupLetterMap,
            manpowerAgg.validateKeys
          )
        : (resolved?.default_shift ?? null);

      if (bucket) {
        groupCounts.set(bucket, (groupCounts.get(bucket) ?? 0) + 1);
      }
    });

    groupCounts.forEach((count, shiftKey) => {
      totals.set(`${dayIndex}-${shiftKey}`, count);
    });
  }

  return totals;
}
